import { productData } from '../../data/mockData';
import { lookupPricingAlias } from '../../data/pricingTaxonomyAliases';

/**
 * Parse Quote Calculator URL / router state into a normalized initial-state
 * object. Keeps QuoteCalculator.jsx focused on rendering + handlers instead
 * of string-mangling URL params.
 *
 *   ?p=stickers&f=die-cut&m=...&w=4&h=4&u=inches&q=500&s=standard&step=4
 *
 * Long param aliases `product=` / `format=` are honoured for backwards
 * compatibility with old deep-links emailed to customers.
 *
 * iter114s.50 — `category=`, `format=`, `finish=` (display-name aliases
 * forwarded by the Pricing Catalog "Custom Size / Qty" link) are also
 * resolved here. The pricing taxonomy uses raw display names ("Decals &
 * Clings", "Static Clings", "White Static Removable Glossy") while the
 * wizard state stores productData IDs ("decals", "static-clings",
 * "clear-static-clings-kiss-cut"). A case-insensitive name → id lookup
 * bridges the gap; partial-match fallbacks handle the few combos where
 * the two taxonomies disagree (e.g. pricing "Die-Cut" vs productData
 * "Die-Cut Sticker Singles"). Unmatched names are dropped silently so
 * the wizard lands on whichever step is reachable instead of crashing.
 */

const _ci = (s) => (s || '').toString().trim().toLowerCase();

const _resolveCategoryId = (displayName) => {
  if (!displayName) return '';
  const target = _ci(displayName);
  const cats = productData?.categories || [];
  // Exact name match wins (e.g. "Stickers" → "stickers", "Labels" → "labels").
  const exact = cats.find((c) => _ci(c.name) === target);
  if (exact) return exact.id;
  // Fall back to substring match — handles "Decals & Clings" → "decals"
  // when productData names diverge slightly. Bias toward the longest
  // overlap so "Decals" doesn't accidentally win over "Decals & Clings".
  const partials = cats.filter((c) =>
    _ci(c.name).includes(target) || target.includes(_ci(c.name)),
  );
  if (partials.length) {
    partials.sort((a, b) => _ci(b.name).length - _ci(a.name).length);
    return partials[0].id;
  }
  return '';
};

const _resolveFormatId = (categoryId, displayName) => {
  if (!categoryId || !displayName) return '';
  const cat = (productData?.categories || []).find((c) => c.id === categoryId);
  if (!cat) return '';
  const target = _ci(displayName);
  const subs = cat.subcategories || [];
  const exact = subs.find((s) => _ci(s.name) === target);
  if (exact) return exact.id;
  const partials = subs.filter((s) =>
    _ci(s.name).includes(target) || target.includes(_ci(s.name)),
  );
  if (partials.length) {
    partials.sort((a, b) => _ci(b.name).length - _ci(a.name).length);
    return partials[0].id;
  }
  return '';
};

const _resolveMaterialId = (categoryId, formatId, displayName) => {
  if (!categoryId || !formatId || !displayName) return '';
  const cat = (productData?.categories || []).find((c) => c.id === categoryId);
  if (!cat) return '';
  const sub = (cat.subcategories || []).find((s) => s.id === formatId);
  if (!sub) return '';
  const target = _ci(displayName);
  const mats = sub.materials || [];
  const exact = mats.find((m) => _ci(m.name) === target);
  if (exact) return exact.id;
  const partials = mats.filter((m) =>
    _ci(m.name).includes(target) || target.includes(_ci(m.name)),
  );
  if (partials.length) {
    partials.sort((a, b) => _ci(b.name).length - _ci(a.name).length);
    return partials[0].id;
  }
  return '';
};

export const parseQuoteParams = (searchString) => {
  // eslint-disable-next-line no-undef
  const p = new URLSearchParams(searchString || '');

  // Disambiguate the two URL shapes:
  //   • Legacy short-codes:  ?p=stickers&f=die-cut-singles&m=clear-vinyl-...
  //   • Pricing Catalog:     ?category=Stickers&format=Die-Cut&finish=White Vinyl…
  // The presence of `category=` flips us into "display-name mode" so the
  // shared `format=` key is interpreted as a name (not a short-code id).
  const isCatalogDeepLink = p.has('category');

  let preselectedProduct = p.get('p') || p.get('product') || '';
  let preselectedFormat = p.get('f') || (isCatalogDeepLink ? '' : p.get('format')) || '';
  let preselectedMaterial = p.get('m') || '';

  if (isCatalogDeepLink) {
    // Fast path — explicit alias for this (category, format, finish)
    // triple wins over the heuristic fallback. Covers every combo
    // currently in `/api/pricing/options`.
    const aliasHit =
      !preselectedProduct && !preselectedFormat && !preselectedMaterial
        ? lookupPricingAlias(p.get('category'), p.get('format'), p.get('finish'))
        : null;
    if (aliasHit) {
      preselectedProduct = aliasHit.product || '';
      preselectedFormat = aliasHit.format || '';
      preselectedMaterial = aliasHit.material || '';
    } else {
      // Heuristic fallback — used when an admin imports a new pricing
      // combo that hasn't been aliased yet, or when the URL only
      // partially identifies a combo.
      if (!preselectedProduct) {
        preselectedProduct = _resolveCategoryId(p.get('category'));
      }
      if (!preselectedFormat && p.get('format') && preselectedProduct) {
        preselectedFormat = _resolveFormatId(preselectedProduct, p.get('format'));
      }
      if (!preselectedMaterial && p.get('finish') && preselectedProduct && preselectedFormat) {
        preselectedMaterial = _resolveMaterialId(preselectedProduct, preselectedFormat, p.get('finish'));
      }
    }
  }

  const qParam = p.get('q');
  const stepParam = parseInt(p.get('step'), 10);

  // Step routing — when the catalog deep-link resolved BOTH a product
  // AND a format (most common case), jump straight to step 3 (material
  // selection). When a material was also resolved, jump to step 4
  // (size/quantity). When only the product was resolved (e.g. the
  // pricing taxonomy "Page (Multiple Designs)" doesn't map cleanly to
  // a productData subcategory), advance to step 2 so the user picks
  // their format. Otherwise honour the `?step=` override or fall back
  // to the legacy "format-only ⇒ step 3, else step 1" rule.
  let derivedStep;
  if (Number.isFinite(stepParam) && stepParam > 0) {
    derivedStep = stepParam;
  } else if (preselectedMaterial) {
    derivedStep = 4;
  } else if (preselectedFormat) {
    derivedStep = 3;
  } else if (preselectedProduct) {
    derivedStep = 2;
  } else {
    derivedStep = 1;
  }

  return {
    product: preselectedProduct,
    format: preselectedFormat,
    material: preselectedMaterial,
    width: p.get('w') || '2',
    height: p.get('h') || '2',
    unit: p.get('u') || 'inches',
    quantity: qParam ? parseInt(qParam, 10) : null,
    shipping: p.get('s') || 'standard',
    step: derivedStep,
  };
};
